<script setup lang="ts">
import { ref, onMounted, watch } from "vue"
import axios from "axios"
import { Pie, Bar } from "vue-chartjs"
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
  CategoryScale,
  LinearScale,
} from "chart.js"

import BottomSheet from './BottomSheet.vue'
import DataTable from './datatable/DataTable.vue'

import { get_data_attr } from '@/utils/globals'
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table"
import { useViewStore } from '@/stores/viewStore'

import rkIconUrl from '@/components/images/rk.svg?url'
import RechtsgrundlagenLogo from '@/components/images/rk.svg'
import PflichtenLogo from '@/components/images/irk.svg'
import { ClipboardCheck } from "lucide-vue-next";

const viewStore = useViewStore();

const customerInfo = get_data_attr("customer");
const userInfo = get_data_attr("user");
const enableEditDashboardTables = !!(userInfo?.is_assigner || userInfo?.is_hoeppner);

// compute column totals
// Register chart.js elements
ChartJS.register(Title, Tooltip, Legend, ArcElement, BarElement, CategoryScale, LinearScale)

// Optional props
const props = defineProps<{
  icon?: object,
  specsInfo: { type: Object }
}>()


// Tabs state
const activeTab = ref("comprg")

const isLoading = ref(true)

const pieData = ref({
  labels: ["Apples", "Bananas", "Cherries"],
  datasets: [
    {
      label: "Fruits",
      data: [40, 30, 30],
      backgroundColor: ["#0088FE", "#00C49F", "#FFBB28"],
    },
  ],
})

// Dummy data for Bar chart
const barData = ref({
  labels: ["Jan", "Feb", "Mar", "Apr"],
  datasets: [
    {
      label: "Sales",
      data: [4000, 3000, 2000, 2780],
      backgroundColor: "#8884d8",
    },
    {
      label: "Profit",
      data: [2400, 1398, 980, 3908],
      backgroundColor: "#82ca9d",
    },
  ],
})

const compliance_rg_ra = ref({});
const compliance_rg = ref({});
const compliance_rg_pieData = ref({
  labels: [],
  datasets: [
    {
      data: [],
      backgroundColor: [],
    },
  ],
})

const compliance_pf_ra = ref({});
const compliance_pf = ref({});
const compliance_pf_pieData = ref({
	labels: [],
	datasets: [
	  {
	    data: [],
	    backgroundColor: [],
	  },
	],
})

const update_status_rg = ref({});
const aktualisierung_rg_pieData = ref({
	labels: [],
	datasets: [
	  {
	    data: [],
	    backgroundColor: [],
	  },
	],
})

const update_status_pf = ref({});
const aktualisierung_pf_pieData = ref({
	labels: [],
	datasets: [
	  {
	    data: [],
	    backgroundColor: [],
	  },
	],
})

const tasks_status_functions = ref({});
const tasks_status_functions_pieData = ref({
	labels: [],
	datasets: [
	  {
	    data: [],
	    backgroundColor: [],
	  },
	],
})

const tasks_status_users = ref({});
const tasks_status_users_pieData = ref({
	labels: [],
	datasets: [
	  {
	    data: [],
	    backgroundColor: [],
	  },
	],
})

const tasks_status_assigned = ref({});
const tasks_status_assigned_pieData = ref({
	labels: [],
	datasets: [
	  {
	    data: [],
	    backgroundColor: [],
	  },
	],
})

const rg_status_labels = ref([])
const pf_status_labels = ref([])
const update_status_labels = ref([])
const task_status_labels = ref([])
const task_status_unassigned_labels = ref([])
const task_status_colors = ref([])
const legal_status_colors_codes = ref([])
const legal_status_colors = ref([])
const legal_status_text_colors = ref([])

async function loadServerData() {
  const { data, headers } = await axios.get("/charts")
  
  rg_status_labels.value              = data["rg_status_labels"] 
  pf_status_labels.value              = data["pf_status_labels"]  
  update_status_labels.value          = data["update_status_labels"]  
  task_status_labels.value            = data["task_status_labels"]
  task_status_unassigned_labels.value = data["task_status_unassigned_labels"]  
  task_status_colors.value            = data["task_status_colors"]  
  legal_status_colors_codes.value     = data["legal_status_colors_codes"]
  legal_status_colors.value           = data["legal_status_colors"]
  legal_status_text_colors.value      = data["legal_status_text_colors"]

  compliance_rg_ra.value              = data["compliance_rg_ra"]
  compliance_rg.value                 = data["compliance_rg"]

  compliance_rg_pieData.value.labels = rg_status_labels.value.map(l => l[0])
  compliance_rg_pieData.value.datasets[0].backgroundColor = rg_status_labels.value.map(l => legal_status_colors_codes.value[l[1]])
  compliance_rg_pieData.value.datasets[0].data = rg_status_labels.value.map(l => complianceLabelValue(compliance_rg.value, l))

  update_status_rg.value = data["update_status_rg"]
  aktualisierung_rg_pieData.value.labels = update_status_labels.value.map(l => l[0])
  aktualisierung_rg_pieData.value.datasets[0].backgroundColor = update_status_labels.value.map(l => legal_status_colors.value[l[1]])
  aktualisierung_rg_pieData.value.datasets[0].data = Object.values(update_status_rg.value)

  if (customerInfo.is_premium) {
	compliance_pf_ra.value = data["compliance_pf_ra"]
	compliance_pf.value    = data["compliance_pf"]

	compliance_pf_pieData.value.labels = pf_status_labels.value.map(l => l[0])
	compliance_pf_pieData.value.datasets[0].backgroundColor = pf_status_labels.value.map(l => legal_status_colors_codes.value[l[1]])
	compliance_pf_pieData.value.datasets[0].data = pf_status_labels.value.map(l => complianceLabelValue(compliance_pf.value, l))

	update_status_pf.value = data["update_status_pf"]
	aktualisierung_pf_pieData.value.labels = update_status_labels.value.map(l => l[0])
	aktualisierung_pf_pieData.value.datasets[0].backgroundColor = update_status_labels.value.map(l => legal_status_colors.value[l[1]])
	aktualisierung_pf_pieData.value.datasets[0].data = Object.values(update_status_pf.value)
  }
  
  tasks_status_functions.value = data["update_status_tasks_functions"];
  tasks_status_functions_pieData.value.labels = task_status_labels.value.map(l => l[0]);
  tasks_status_functions_pieData.value.datasets[0].backgroundColor = task_status_labels.value.map(l => task_status_colors.value[l[1]])
  tasks_status_functions_pieData.value.datasets[0].data = Object.values(tasks_status_functions.value)

  tasks_status_users.value = data["update_status_tasks_users"];
  tasks_status_users_pieData.value.labels = task_status_labels.value.map(l => l[0]);
  tasks_status_users_pieData.value.datasets[0].backgroundColor = task_status_labels.value.map(l => task_status_colors.value[l[1]])
  tasks_status_users_pieData.value.datasets[0].data = Object.values(tasks_status_users.value)

  tasks_status_assigned.value = data["task_status_assigned_vs_unassigned"];
  tasks_status_assigned_pieData.value.labels = task_status_unassigned_labels.value.map(l => l[0]);
  tasks_status_assigned_pieData.value.datasets[0].backgroundColor = task_status_unassigned_labels.value.map(l => l[1] === 3 ? 'green' : 'red')
  tasks_status_assigned_pieData.value.datasets[0].data = Object.values(tasks_status_assigned.value)

  console.log("compliance_rg_ra.value=" + JSON.stringify(compliance_rg_ra.value))
  console.log("compliance_rg.value=" + JSON.stringify(compliance_rg.value))
  console.log("rg_status_labels.value=" + JSON.stringify(rg_status_labels.value))
  console.log("pf_status_labels.value=" + JSON.stringify(pf_status_labels.value))
  console.log("update_status_labels.value" + JSON.stringify(update_status_labels.value))
  console.log("task_status_unassigned_labels.value=" + JSON.stringify(task_status_unassigned_labels.value))
  console.log("legal_status_colors_codes.value=" + JSON.stringify(legal_status_colors_codes.value))
  console.log("task_status_colors.value=" + JSON.stringify(task_status_colors.value))
  console.log("legal_status_text_colors.value=" + JSON.stringify(legal_status_text_colors.value))
  console.log("compliance_rg_pieData.value=" + JSON.stringify(compliance_rg_pieData.value))

  console.log("compliance_pf_ra.value=" + JSON.stringify(compliance_pf_ra.value))  
  isLoading.value = false;
}

const getCellStyle = (v, label) => {
  let idx = label[1];
  let is_colored = v[idx.toString()] > 0;
  return {
    backgroundColor: is_colored ? legal_status_colors_codes.value[idx] : "white",
	color: is_colored ? legal_status_text_colors.value[idx] : "white",
	cursor: "pointer"
  }
}

const setComplianceColor = (labels, k) => {
	const label = labels.find(l => l[0] === k)
	if (!label) return {}
	const idx = label[1]
	return {
	  backgroundColor: legal_status_colors_codes.value[idx],
	  color: legal_status_text_colors.value[idx]
	}	
}

function complianceLabelValue(compliance, label) {
  return Number((compliance || {})[label?.[0]] || 0)
}

function complianceStatusValue(compliance, label) {
  return Number((compliance || {})[label?.[1]?.toString()] || 0)
}

function getTotal(v) {
  return Object.values(v).reduce((acc, val) => acc + val, 0)
}

function getTotalPercent(v, compliance) {
  const total = getTotal(compliance)
  return total === 0 ? 0 : Math.round(100 * v / total)
}

onMounted(async () => {
  await loadServerData();  
  activeTab.value = viewStore.chartsActiveTab;
  setupCharts();
})

watch(activeTab, (newVal) => {
  viewStore.chartsActiveTab = newVal;  
})


const isPaneRgComplianceOpen = ref(false)
const chartRgComplianceRef = ref(null)
const rg_status = ref([])
const rg_ra = ref([])

const isPanePfComplianceOpen = ref(false)
const chartPfComplianceRef = ref(null)
const pf_status = ref([])
const pf_ra = ref([])

const isPaneAktRgOpen = ref(false)
const chartAktRgRef = ref(null)
const akt_rg_status = ref([])

const isPaneAktPfOpen = ref(false)
const chartAktPfRef = ref(null)
const akt_pf_status = ref([])


const isPaneTasksOpen = ref(false);
const chartTaskFunctionsRef = ref(null)
const chartTaskUsersRef = ref(null)
const chartTaskUnassignedRef = ref(null)
const tasks_status = ref([])
const chart_type = ref(null);

const selectedCategory = ref(null)

function getStatus(labels, label) {
  return labels.value.find(l => l[0] === label)?.[1]
}

function setupChart(chartRef) {
	const chart = chartRef.value?.chart
	if (!chart) return

	chart.canvas.style.cursor = "pointer"
	chart.canvas.onclick = (event) => {
	    const points = chart.getElementsAtEventForMode(
	      event,
	      'nearest',
	      { intersect: true },
	      true
	    )

	    if (!points.length) return // nothing clicked

	    const idx = points[0].index
	    const label = chart.data.labels[idx]

		const chartName = chart.options.chartName
		console.log("Clicked chart:", chartName + " idx=" + idx + " label=" + label)
		
		switch (chartName) {
		case "comprg": {
		  const stRg = getStatus(rg_status_labels, label)
		  if (stRg === undefined) return
		  chart_type.value = 0
		  rg_status.value = stRg
		  rg_ra.value = null
		  isPaneRgComplianceOpen.value = true
		  isPanePfComplianceOpen.value = false
		  isPaneAktRgOpen.value = false
		  isPaneAktPfOpen.value = false
		  isPaneTasksOpen.value = false
		  break
		}
		case "comppf": {
		  const stPf = getStatus(pf_status_labels, label)
		  if (stPf === undefined) return
		  chart_type.value = 1
		  pf_status.value = stPf
		  pf_ra.value = null
		  isPaneRgComplianceOpen.value = false
		  isPanePfComplianceOpen.value = true
		  isPaneAktRgOpen.value = false
		  isPaneAktPfOpen.value = false
		  isPaneTasksOpen.value = false
		  break
		}
		case "aktrg": {
		  const stAktRg = getStatus(update_status_labels, label)
		  if (stAktRg === undefined) return
		  chart_type.value = 2
		  akt_rg_status.value = stAktRg
		  isPaneRgComplianceOpen.value = false
		  isPanePfComplianceOpen.value = false
		  isPaneAktRgOpen.value = true
		  isPaneAktPfOpen.value = false
		  isPaneTasksOpen.value = false
		  break
		}
		case "aktpf": {
		  const stAktPf = getStatus(update_status_labels, label)
		  if (stAktPf === undefined) return
		  chart_type.value = 3
		  akt_pf_status.value = stAktPf
		  isPaneRgComplianceOpen.value = false
		  isPanePfComplianceOpen.value = false
		  isPaneAktRgOpen.value = false
		  isPaneAktPfOpen.value = true
		  isPaneTasksOpen.value = false
		  break
		}
		case "tasksFunctions": {
		  const stTf = getStatus(task_status_labels, label)
		  if (stTf === undefined) return
		  chart_type.value = 4
		  tasks_status.value = stTf
		  isPaneRgComplianceOpen.value = false
		  isPanePfComplianceOpen.value = false
		  isPaneAktRgOpen.value = false
		  isPaneAktPfOpen.value = false
		  isPaneTasksOpen.value = true
		  break
		}
	    case "tasksUsers": {
		  const stTu = getStatus(task_status_labels, label)
		  if (stTu === undefined) return
		  chart_type.value = 5
	      tasks_status.value = stTu
	      isPaneRgComplianceOpen.value = false
	      isPanePfComplianceOpen.value = false
		  isPaneAktRgOpen.value = false
		  isPaneAktPfOpen.value = false
	      isPaneTasksOpen.value = true
	      break
		}
	    case "tasksAssignments": {
		  const stAs = getStatus(task_status_unassigned_labels, label)
		  if (stAs === undefined) return
		  chart_type.value = 6
	      tasks_status.value = stAs
	      isPaneRgComplianceOpen.value = false
	      isPanePfComplianceOpen.value = false
		  isPaneAktRgOpen.value = false
		  isPaneAktPfOpen.value = false
	      isPaneTasksOpen.value = true
	      break
		}
	    }
		selectedCategory.value = label
	  }
}

function setupCharts() {
  setupChart(chartRgComplianceRef);
  setupChart(chartPfComplianceRef);
  setupChart(chartAktRgRef);
  setupChart(chartAktPfRef);
  setupChart(chartTaskFunctionsRef);
  setupChart(chartTaskUsersRef);
  setupChart(chartTaskUnassignedRef);
}

function getChartOptions(name, title) {	
	return { plugins: {title: {display: true, text: title, font: { size: 24, weight: 'bold' }}}, chartName: name }
}

function complianceRgColumnClick(columnKey) {
  const row = rg_status_labels.value.find(l => l[0] === columnKey)
  if (row) openCompliancePane(null, row[1])
}

function openCompliancePane(k, v, is_rg = true) {
    console.log("openCompliancePane=" + k + " v=" + JSON.stringify(v))

	if (is_rg) {
	  rg_ra.value = k
	  rg_status.value = v
	  isPaneRgComplianceOpen.value = true
	  pf_ra.value = null
	  pf_status.value = null
	  isPanePfComplianceOpen.value = false
	} else {
	  rg_ra.value = null
	  rg_status.value = null
	  isPaneRgComplianceOpen.value = false
	  pf_ra.value = k
	  pf_status.value = v
	  isPanePfComplianceOpen.value = true
	}
}
</script>

<template>
  <div class="p-6">
	
	<h2 class="text-xl font-bold flex items-center gap-2">
		<component :is="props.icon" class="w-8 h-8 mb-2" v-if="props.icon" />
	  <span>Dashboard</span>
	</h2>
    <!-- Tabs -->
    <div class="flex gap-4 border-b mb-4">
		<button
		  class="pb-2 border-b-2"
		  :class="activeTab === 'comprg' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'"
		  @click="activeTab = 'comprg'"
		>
		  Compliance Rechtsgrundlagen
		</button>
      <button v-if="customerInfo.is_premium"
        class="pb-2 border-b-2"
        :class="activeTab === 'comppf' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'"
        @click="activeTab = 'comppf'"
      >
        Compliance Pflichten
      </button>
      <button
        class="pb-2 border-b-2"
        :class="activeTab === 'aktrg' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'"
        @click="activeTab = 'aktrg'"
      >
        Aktualisierung Rechtsgrundlagen
      </button>
	  <button
	    class="pb-2 border-b-2"
	    :class="activeTab === 'aktpf' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'"
	    @click="activeTab = 'aktpf'"
	  >
	    Aktualisierung Pflichten
	  </button>
	  <button
	    class="pb-2 border-b-2"
	    :class="activeTab === 'tasks' ? 'border-blue-500 text-blue-600' : 'border-transparent text-gray-500'"
	    @click="activeTab = 'tasks'"
	  >
	    Aufgaben
	  </button>
    </div>

    <!-- Tab content -->
	<div v-show="activeTab === 'comprg'" class="w-full p-6 bg-white shadow rounded-2xl" >
		<div class="flex gap-6 chart-pane">
		    <div class="rounded-lg border shadow"  v-if="!isLoading">
				<Table class="text-center table-fixed border-collapse !w-auto">
		        <TableHeader>
				  <TableRow>
					<TableHead class="text-left"></TableHead>
					<TableHead v-for="labels in rg_status_labels" :key="labels[0]" class="text-center">{{ labels[0] }}</TableHead>
					<TableHead>Total</TableHead>
				  </TableRow>
				  <TableRow>
				    <TableHead class="text-center"></TableHead>
					<TableHead v-for="label in rg_status_labels" :key="label[1]" class="text-center">{{ getTotalPercent(complianceLabelValue(compliance_rg, label), compliance_rg) }}%</TableHead>
				    <TableHead class="text-center">100%</TableHead>
				  </TableRow>
		          <TableRow>
		            <TableHead class="text-left">Rechtsaspekt</TableHead>
					
		            <TableHead v-for="label in rg_status_labels" :key="label[1]" :style="setComplianceColor(rg_status_labels, label[0])" @click="openCompliancePane(null, label[1])" class="text-center"  style="cursor: pointer">{{ complianceLabelValue(compliance_rg, label) }}</TableHead>
				    <TableHead class="text-center"  @click="openCompliancePane(null, null)" style="cursor: pointer">{{getTotal(compliance_rg)}}</TableHead>
		          </TableRow>
		        </TableHeader>

				<TableBody>
					<TableRow v-for="[k, v] in Object.entries(compliance_rg_ra)" :key="k">
					<TableCell class="font-medium text-left truncate">{{ k }}</TableCell>
					
					<TableCell 
						v-for="label in rg_status_labels" 
						class="font-medium" 
						:style="getCellStyle(v, label)"
						@click="openCompliancePane(k, label[1])"  style="cursor: pointer"
					>
						{{ complianceStatusValue(v, label) }}
					</TableCell>
					
					<TableCell class="font-medium" @click="openCompliancePane(k, null)" style="cursor: pointer">
						{{ getTotal(v) }}
					</TableCell>
					</TableRow>
				</TableBody>
				</Table>
		    </div>

		    <!-- Pie Chart -->
		    <div class="w-[400px] h-[400px]" v-if="!isLoading">
				<Pie ref="chartRgComplianceRef" :data="compliance_rg_pieData" :options="getChartOptions('comprg', 'Compliance Rechtsgrundlagen')" />
		    </div>

			<BottomSheet v-model="isPaneRgComplianceOpen">
			  <div style="height: 100%; overflow-y: hidden">
			    <div style="height: 100%; overflow-y: hidden">
				  <DataTable label="view.rechtsgrundlagen" 
					         labelSingular="view.rechtsgrundlage"
					         :logo="rkIconUrl"
					         :key="rg_status"
							 endpoint="rechtsgrundlagen"
							 assignmentScopeArea="dashboard"
							 :endpointpars="{chart_type: 0, Rechtsaspekt: [rg_ra], status: [rg_status]}"
							 v-bind="{ ...(!customerInfo.is_hoeppner ? { saveendpoint: 'rechtsgrundlagenmatrix' } : {}) }"
					         :columns="specsInfo['rechtsgrundlagen']"
							 :enableColFilterSide="false"
							 :enableAdd="false"
							 :enableEdit="enableEditDashboardTables"
							 :enableDelete="false"
							 :headerFontSize="14">
					    <template #logo><RechtsgrundlagenLogo  class="w-8 h-8"/></template>
                  </DataTable>
                </div>
              </div>
			</BottomSheet>
							
		 </div>
	</div>
    <div v-show="activeTab === 'comppf'" class="w-full p-6 bg-white shadow rounded-2xl">
		<div class="flex gap-6 chart-pane">
		    <div class="w-[810px] rounded-lg border shadow"  v-if="!isLoading">
				<Table class="text-center table-fixed border-collapse !w-auto">
				<TableHeader>
					<TableRow>
					<TableHead class="text-left w-64">Rechtsaspekt</TableHead>
					<TableHead 
						v-for="labels in pf_status_labels" 
						:key="labels[0]" 
						class="text-center w-32"
					>
						{{ labels[0] }}
					</TableHead>
					<TableHead class="text-center w-32">Total</TableHead>
					</TableRow>

					<TableRow>
					<TableHead class="text-center"></TableHead>
					<TableHead 
						v-for="label in pf_status_labels"
						:key="label[1]"
						class="text-center"
					>
						{{ getTotalPercent(complianceLabelValue(compliance_pf, label), compliance_pf) }}%
					</TableHead>
					<TableHead class="text-center">100%</TableHead>
					</TableRow>

					<TableRow>
					<TableHead class="text-left">Rechtsaspekt</TableHead>
					<TableHead 
						v-for="label in pf_status_labels"
						:key="label[1]"
						:style="setComplianceColor(pf_status_labels, label[0])" 
						class="text-center"
						@click="openCompliancePane(null, label[1], false)"   style="cursor: pointer"
					>

						{{ complianceLabelValue(compliance_pf, label) }}
					</TableHead>
					<TableHead class="text-center" @click="openCompliancePane(null, null, false)"  style="cursor: pointer">{{ getTotal(compliance_pf) }}</TableHead>
					</TableRow>
				</TableHeader>

				<TableBody>
					<TableRow v-for="[k, v] in Object.entries(compliance_pf_ra)" :key="k">
					<TableCell class="font-medium text-left truncate">{{ k }}</TableCell>
					
					<TableCell 
						v-for="label in pf_status_labels" 
						:key="label[1]"
						class="font-medium" 
						:style="getCellStyle(v, label)" 
						@click="openCompliancePane(k, label[1], false)"
					>
						{{ complianceStatusValue(v, label) }}
					</TableCell>
					
					<TableCell 
						class="font-medium cursor-pointer" 
						@click="openCompliancePane(k, null, false)"
					>
						{{ getTotal(v) }}
					</TableCell>
					</TableRow>
				</TableBody>
				</Table>
		    </div>

		    <div class="w-[400px] h-[400px]"  v-if="!isLoading">
				<Pie ref="chartPfComplianceRef" :data="compliance_pf_pieData" :options="getChartOptions('comppf', 'Compliance Pflichten')" />
		    </div>
			
			<BottomSheet v-model="isPanePfComplianceOpen">
			  <div style="height: 100%; overflow-y: hidden">
			    <div style="height: 100%; overflow-y: hidden">
				  <DataTable label="view.pflichten" 
					         labelSingular="view.pflicht"
					         :logo="rkIconUrl"
					         :key="pf_status"
							 endpoint="pflichten"
							 assignmentScopeArea="dashboard"
							 :endpointpars="{chart_type: 1, Rechtsaspekt: [pf_ra], status: [pf_status], reset_filter: 1}"
							 v-bind="{ ...(!customerInfo.is_hoeppner ? { saveendpoint: 'pflichtenmatrix' } : {}) }"
					         :columns="specsInfo['pflichten']"
							 :enableColFilterSide="false"
							 :enableAdd="false"
							 :enableEdit="enableEditDashboardTables"
							 :enableDelete="false"
							 :headerFontSize="14">
					    <template #logo><PflichtenLogo  class="w-8 h-8"/></template>
			      </DataTable>
			    </div>
			  </div>
			</BottomSheet>
		  </div>
    </div>
	<div v-show="activeTab === 'aktrg'" class="w-full p-6 bg-white shadow rounded-2xl">
		<div class="flex gap-6 chart-pane">
		    <div class="w-[400px] h-[400px]"  v-if="!isLoading">
				<Pie ref="chartAktRgRef" :data="aktualisierung_rg_pieData" :options="getChartOptions('aktrg', 'Aktualisierung Rechtsgrundlagen')"/>
		    </div>
			<BottomSheet v-model="isPaneAktRgOpen">
			  <div style="height: 100%; overflow-y: hidden">
			    <div style="height: 100%; overflow-y: hidden">
					<DataTable label="view.rechtsgrundlagen" 
					         labelSingular="view.rechtsgrundlage"
					         :logo="rkIconUrl"
					         :key="akt_rg_status"
							  endpoint="rechtsgrundlagen"
							 assignmentScopeArea="dashboard"
							 :endpointpars="{chart_type: 3, aktrg: [akt_rg_status]}"
							 v-bind="{ ...(!customerInfo.is_hoeppner ? { saveendpoint: 'rechtsgrundlagenmatrix' } : {}) }"
					         :columns="specsInfo['rechtsgrundlagen']"
							 :enableColFilterSide="false"
							 :enableAdd="false"
							 :enableEdit="enableEditDashboardTables"
							 :enableDelete="false"
							 :headerFontSize="14">
					    <template #logo><RechtsgrundlagenLogo  class="w-8 h-8"/></template>
					</DataTable>
			    </div>
			  </div>
			</BottomSheet>
		  </div>
	</div>
    <div v-show="activeTab === 'aktpf'" class="w-full p-6 bg-white shadow rounded-2xl">
	    <div class="flex gap-6 chart-pane">
	        <div class="w-[400px] h-[400px]"  v-if="!isLoading">
			    <Pie ref="chartAktPfRef" :data="aktualisierung_pf_pieData" :options="getChartOptions('aktpf', 'Aktualisierung Pflichten')" />
	        </div>
			<BottomSheet v-model="isPaneAktPfOpen">
			  <div style="height: 100%; overflow-y: hidden">
			    <div style="height: 100%; overflow-y: hidden">
					<DataTable label="view.pflichten"
					         labelSingular="view.pflicht"
					         :logo="rkIconUrl"
					         :key="akt_pf_status"
							  endpoint="pflichten"
							 assignmentScopeArea="dashboard"
							 :endpointpars="{chart_type: 4, aktpf: [akt_pf_status]}"
 							 v-bind="{ ...(!customerInfo.is_hoeppner ? { saveendpoint: 'pflichtenmatrix' } : {}) }"
					         :columns="specsInfo['pflichten']"
							 :enableColFilterSide="false"
							 :enableAdd="false"
							 :enableEdit="enableEditDashboardTables"
							 :enableDelete="false"
							 :headerFontSize="14">
					    <template #logo><PflichtenLogo  class="w-8 h-8"/></template>
					</DataTable>
			    </div>
			  </div>
			</BottomSheet>

	    </div>
    </div>
	<div v-show="activeTab === 'tasks'" class="w-full p-6 bg-white shadow rounded-2xl">
	    <div class="flex gap-6 chart-pane">
	        <div class="w-[400px] h-[400px]"  v-if="!isLoading">
			    <Pie ref="chartTaskFunctionsRef" :data="tasks_status_functions_pieData" :options="getChartOptions('tasksFunctions', 'Aufgaben/Funktion')"/>
	        </div>
			<div class="w-[400px] h-[400px]"  v-if="!isLoading">
			   <Pie ref="chartTaskUsersRef" :data="tasks_status_users_pieData" :options="getChartOptions('tasksUsers', 'Aufgaben/NutzerInnen')"/>
			</div>
		    <div class="w-[400px] h-[400px]"  v-if="!isLoading">
		       <Pie ref="chartTaskUnassignedRef" :data="tasks_status_assigned_pieData" :options="getChartOptions('tasksAssignments', 'Zugewiesen/nicht zugewiesen')"/>
		    </div>

			<BottomSheet v-model="isPaneTasksOpen">
			  <div style="height: 100%; overflow-y: hidden">
			    <div style="height: 100%; overflow-y: hidden">
					<DataTable
					  label="view.tasks"
					  labelSingular="view.task"
					  :logo="rkIconUrl"
					  endpoint="tasks"				
					  assignmentScopeArea="dashboard"
					  :key="tasks_status"
					  :endpointpars="{chart_type: chart_type, status: tasks_status}"
					  :columns="specsInfo['tasks']"
					  :enableColFilterSide="false"
					  :enableAdd="false"
					  :enableEdit="false"
					  :enableDelete="false"
					  :headerFontSize="14">
					  <template #logo><ClipboardCheck /></template>
					</DataTable>
			    </div>
			  </div>
			</BottomSheet>
	    </div>
	</div>
  </div>
  
</template>
